export const escAttr = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
export const escText = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

export interface MetaInput {
  title: string; description: string; keywords: string; author: string; url: string; image: string; lang: string;
  /** Legacy single switch: "noindex" means noindex + nofollow. Prefer the two flags below. */
  robots?: "index" | "noindex";
  noindex?: boolean; nofollow?: boolean;
  siteName?: string; locale?: string; twitterSite?: string; themeColor?: string;
}

/** True only for an absolute http(s) URL with a host. Rejects javascript:, data:, relative paths and plain text. */
export function isAbsoluteHttpUrl(v: string): boolean {
  const t = v.trim();
  if (!/^https?:\/\//i.test(t)) return false;
  try { const u = new URL(t); return (u.protocol === "http:" || u.protocol === "https:") && u.hostname.includes(".") ; } catch { return false; }
}

/** "@name" or "name" -> "@name"; empty when it isn't a plausible X/Twitter handle. */
export function normalizeHandle(v: string): string {
  const t = v.trim().replace(/^@/, "");
  return /^\w{1,15}$/.test(t) ? `@${t}` : "";
}

export const isHexColor = (v: string) => /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(v.trim());

/** Problems with the URL-like and handle-like fields; the matching tags are left out of the output while a field is invalid. */
export function metaProblems(m: Pick<MetaInput, "url" | "image"> & Partial<Pick<MetaInput, "twitterSite" | "themeColor">>): Partial<Record<"url" | "image" | "twitterSite" | "themeColor", string>> {
  const out: Partial<Record<"url" | "image" | "twitterSite" | "themeColor", string>> = {};
  if (m.url.trim() && !isAbsoluteHttpUrl(m.url)) out.url = "Enter a full web address starting with http:// or https://. The canonical and og:url tags are left out until it is valid.";
  if (m.image.trim() && !isAbsoluteHttpUrl(m.image)) out.image = "Enter a full image address starting with http:// or https://. The og:image and twitter:image tags are left out until it is valid.";
  if ((m.twitterSite ?? "").trim() && !normalizeHandle(m.twitterSite!)) out.twitterSite = "Use an X/Twitter handle such as @yourbrand (letters, numbers, underscore).";
  if ((m.themeColor ?? "").trim() && !isHexColor(m.themeColor!)) out.themeColor = "Use a hex color such as #1a73e8.";
  return out;
}

/** Builds head tags. Every value is escaped so quotes, ampersands and angle brackets can't break the markup. Invalid URLs, handles and colors are omitted. */
export function buildMetaTags(m: MetaInput): string {
  const bad = metaProblems(m);
  const url = bad.url ? "" : m.url.trim();
  const image = bad.image ? "" : m.image.trim();
  const handle = bad.twitterSite ? "" : normalizeHandle(m.twitterSite ?? "");
  const color = bad.themeColor ? "" : (m.themeColor ?? "").trim();
  const noindex = m.noindex ?? m.robots === "noindex";
  const nofollow = m.nofollow ?? m.robots === "noindex";
  const robots = [noindex && "noindex", nofollow && "nofollow"].filter(Boolean).join(", ");
  const locale = (m.locale ?? "").trim();
  const siteName = (m.siteName ?? "").trim();
  const lines: (string | false)[] = [
    '<meta charset="UTF-8">',
    '<meta name="viewport" content="width=device-width, initial-scale=1.0">',
    m.title.trim() && `<title>${escText(m.title.trim())}</title>`,
    m.description.trim() && `<meta name="description" content="${escAttr(m.description.trim())}">`,
    m.keywords.trim() && `<meta name="keywords" content="${escAttr(m.keywords.trim())}">`,
    m.author.trim() && `<meta name="author" content="${escAttr(m.author.trim())}">`,
    robots && `<meta name="robots" content="${robots}">`,
    color && `<meta name="theme-color" content="${escAttr(color)}">`,
    url && `<link rel="canonical" href="${escAttr(url)}">`,
    '<meta property="og:type" content="website">',
    siteName && `<meta property="og:site_name" content="${escAttr(siteName)}">`,
    locale && `<meta property="og:locale" content="${escAttr(locale)}">`,
    m.title.trim() && `<meta property="og:title" content="${escAttr(m.title.trim())}">`,
    m.description.trim() && `<meta property="og:description" content="${escAttr(m.description.trim())}">`,
    url && `<meta property="og:url" content="${escAttr(url)}">`,
    image && `<meta property="og:image" content="${escAttr(image)}">`,
    `<meta name="twitter:card" content="${image ? "summary_large_image" : "summary"}">`,
    handle && `<meta name="twitter:site" content="${escAttr(handle)}">`,
    m.title.trim() && `<meta name="twitter:title" content="${escAttr(m.title.trim())}">`,
    m.description.trim() && `<meta name="twitter:description" content="${escAttr(m.description.trim())}">`,
    image && `<meta name="twitter:image" content="${escAttr(image)}">`,
  ];
  return lines.filter(Boolean).join("\n");
}
